import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadInvoices from "@insite/client-framework/Store/Data/Invoices/Handlers/LoadInvoices";
import { InvoicesDataViewContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/InvoiceHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import { InvoiceCollectionModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Link from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import adiDeleteInvoicePaymentRecap from "@NA/Handlers/Invoices/ADIDeleteInvoicePaymentRecap";
import adiLoadInvoicePaymentRecap from "@NA/Handlers/Invoices/ADILoadInvoicePaymentRecap";
import MailIcon from "@NA/Icons/MailIcon";
import TrashIcon from "@NA/Icons/TrashIcon";
import { InvoicePaymentModel } from "@NA/Services/ADIInvoiceService";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import { formatDate, formatPhoneNumber } from "@NA/Utils/Utility";
import * as React from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => ({
    invoicePaymentRecapData: state.ADIGlobalData.adiInvoicePaymentRecap.InvoicePaymentRecap,
    isLoading: state.ADIGlobalData.adiInvoicePaymentRecap.isLoading,
    currencySymbol: state.context.session.currency?.currencySymbol ?? "",
    invoicesLink: getPageLinkByPageType(state, "InvoiceHistoryPage")?.url,
    isPaymentCompleted: state.ADIGlobalData.adiInvoicePaymentRecap.isPaymentCompleted,
});

const mapDispatchToProps = {
    adiLoadInvoicePaymentRecap,
    adiDeleteInvoicePaymentRecap,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceRecapStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    mainContainer?: InjectableCss;
    desktopWrapper?: InjectableCss;
    creditRepContainer?: InjectableCss;
    invoiceInfoHeaderContainer?: InjectableCss;
    invoiceInfoDataContainer?: InjectableCss;
    invoiceInfoDataDesktopContainer?: InjectableCss;
    invoiceInfoDataTabletContainer?: InjectableCss;
    dateInfoHeader?: InjectableCss;
    dateInfoData?: InjectableCss;
    typeInfoData?: InjectableCss;
    invoiceInfoData?: InjectableCss;
    poInfoData?: InjectableCss;
    dueDateInfoData?: InjectableCss;
    balanceInfoData?: InjectableCss;
    paymentInfoData?: InjectableCss;
    paymentInfoDataForTablet?: InjectableCss;
    deleteInfoData?: InjectableCss;
    deleteInfoDataForTablet?: InjectableCss;
    typeInfoHeader?: InjectableCss;
    invoiceInfoHeader?: InjectableCss;
    poInfoHeader?: InjectableCss;
    dueDateInfoHeader?: InjectableCss;
    balanceInfoHeader?: InjectableCss;
    paymentInfoHeader?: InjectableCss;
    deleteInfoHeader?: InjectableCss;
    paymentTotalContainer?: InjectableCss;
    totalAmountText?: InjectableCss;
    totalAmountData?: InjectableCss;
    deleteButtonStyle?: ButtonPresentationProps;
    invoiceContainerForTablet?: InjectableCss;
    eachInvoiceContainerForMobile?: InjectableCss;
    invoiceForMobileRowOne?: InjectableCss;
    invoiceForMobileRowTwo?: InjectableCss;
    mobileSpanStyle?: InjectableCss;
    invoiceForMobileRowThree?: InjectableCss;
    emailIcon?: InjectableCss;
}

export const invoiceRecapStyles: InvoiceRecapStyles = {
    centeringWrapper: {
        css: css`
            height: 150px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        size: 16,
        weight: 600,
    },
    mainContainer: {
        css: css`
            margin-top: 30px;
        `,
    },
    desktopWrapper: {
        css: css``,
    },
    creditRepContainer: {
        css: css`
            display: flex;
            align-items: center;
            font-size: 14px;
            font-weight: 600;
            color: ${ADIColors.gray3};
            @media print {
                font-size: 12px;
            }
            span {
                color: ${getColor("text.main")};
                margin: 0 5px;
                @media print {
                    display: inline;
                }
            }
            span:first-child {
                text-transform: capitalize;
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                            display: block;
                            @media print {
                                display: inline;
                            }
                        `,
                    css`
                            display: block;
                            @media print {
                                display: inline;
                            }
                        `,
                    null,
                    null,
                    null,
                ])}
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: block;
                    `,
                    css`
                        display: block;
                    `,
                    css`
                        justify-content: center;
                        padding-bottom: 20px;
                        @media print {
                            justify-content: flex-start;
                        }
                    `,
                    css`
                        justify-content: flex-end;
                        padding-bottom: 36px;
                    `,
                    css`
                        justify-content: flex-end;
                        padding-bottom: 36px;
                    `,
                ])}
        `,
    },
    invoiceInfoHeaderContainer: {
        css: css`
            display: flex;
            flex-wrap: nowrap;
            background: ${ADIColors.white};
            font-size: 16px;
            font-weight: bold;
            position: relative;
            @media print {
                font-size: 12px;
                > div {
                    width: calc(100% / 7) !important;
                    margin: 0;
                    flex-basis: calc(100% / 7) !important;
                    min-width: calc(100% / 7) !important;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding: 12px 40px;
                        margin-bottom: 12px;
                        margin-top: 12px;
                        margin-bottom: 12px;
                        margin-top: 12px;
                        padding-right: 40px;
                    `,
                    css`
                        padding: 12px 40px;
                        margin-bottom: 12px;
                        margin-top: 12px;
                        margin-bottom: 12px;
                        margin-top: 12px;
                        padding-right: 0;
                    `,
                    css`
                        padding: 12px 40px;
                        margin-bottom: 12px;
                        margin-top: 12px;
                        margin-bottom: 12px;
                        margin-top: 12px;
                        padding-right: 0;
                    `,
                ])}
        `,
    },
    invoiceInfoDataContainer: {
        css: css`
            background-color: ${ADIColors.white};
            width: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        margin-bottom: 8px;
                        padding-right: 40px;
                        padding-left: 40px;
                        padding-top: 15px;
                        padding-bottom: 15px;
                    `,
                    css`
                        margin-bottom: 8px;
                        padding-right: 0;
                        padding-left: 40px;
                        padding-top: 15px;
                        padding-bottom: 15px;
                    `,
                    css`
                        margin-bottom: 8px;
                        padding-right: 0;
                        padding-left: 40px;
                        padding-top: 15px;
                        padding-bottom: 15px;
                    `,
                ])}
        `,
    },
    invoiceInfoDataDesktopContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding: 10px 0 20px 0;
                    `,
                    null,
                    null,
                ])}
            display: flex;
            flex-direction: row;
            width: 100%;
            @media print {
                font-size: 12px;
                > div {
                    width: calc(100% / 7) !important;
                    margin: 0;
                    flex-basis: calc(100% / 7) !important;
                    min-width: calc(100% / 7) !important;
                }
            }
        `,
    },
    invoiceInfoDataTabletContainer: {
        css: css`
            @media print {
                display: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        display: flex;
                        width: 100%;
                        flex-direction: row-reverse;
                        border-top: 1px solid ${ADIColors.gray2};
                        padding-top: 17px;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                ])}
        `,
    },
    dateInfoHeader: {
        css: css`
            flex-basis: 13.814%;
            width: 13.814%;
            text-transform: capitalize;
            padding-right: 24px;
            font-size: 16px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.13;
            letter-spacing: normal;
            color: ${getColor("text.main")} !important;
            display: flex;
            flex-flow: row nowrap;
            flex-grow: 1;
            @media print {
                font-size: 12px;
                padding: 15px 0;
            }
        `,
    },
    dateInfoData: {
        css: css`
            flex-basis: calc(13.814% - 5px);
            width: calc(13.814% - 5px);
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            text-transform: capitalize;
            display: flex;
            flex-flow: row nowrap;
            flex-grow: 1;
            white-space: normal;
            justify-content: flex-start;
            align-items: center;
            @media print {
                font-size: 12px;
                padding: 15px 0;
            }
        `,
    },
    typeInfoData: {
        css: css`
            flex-basis: 12.22%;
            width: 12.22%;
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            text-transform: capitalize;
            display: flex;
            flex-flow: row nowrap;
            flex-grow: 1;
            white-space: normal;
            justify-content: flex-start;
            align-items: center;
            @media print {
                font-size: 12px;
                padding: 15px 0;
            }
            > span {
                color: ${ADIColors.blue7};
                padding: 5px 8px 4px 8px;
                border-radius: 8px;
                border: solid 1px ${ADIColors.blue7};
                font-size: 14px;
                font-weight: normal;
                font-stretch: normal;
                font-style: normal;
                line-height: 1;
                letter-spacing: normal;
                @media print {
                    font-size: 12px;
                    padding: 0;
                    border: none;
                    color: ${getColor("text.main")};
                }
            }
        `,
    },
    invoiceInfoData: {
        css: css`
            flex-basis: 8.94%;
            width: 8.94%;
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            text-transform: capitalize;
            display: flex;
            flex-flow: row nowrap;
            flex-grow: 1;
            white-space: normal;
            justify-content: flex-start;
            align-items: center;
            @media print {
                font-size: 12px;
                padding: 15px 0;
            }
        `,
    },
    poInfoData: {
        css: css`
            flex-basis: 15.944%;
            width: 15.944%;
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            text-transform: capitalize;
            display: flex;
            flex-flow: row nowrap;
            flex-grow: 1;
            white-space: normal;
            justify-content: flex-start;
            align-items: center;
            @media print {
                font-size: 12px;
                padding: 15px 0;
                margin-left: 10px;
            }
        `,
    },
    dueDateInfoData: {
        css: css`
            flex-basis: 6.76%;
            width: 6.76%;
            max-width: 149px;
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            text-transform: capitalize;
            display: flex;
            flex-flow: row nowrap;
            flex-grow: 1;
            white-space: normal;
            justify-content: flex-start;
            align-items: center;
            @media print {
                font-size: 12px;
                padding: 15px 0;
                flex-basis: 15%;
                width: 15%;
            }
        `,
    },
    balanceInfoData: {
        css: css`
            flex-basis: 9.91%;
            width: 9.91%;
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            text-transform: capitalize;
            display: flex;
            flex-flow: row nowrap;
            flex-grow: 1;
            white-space: normal;
            justify-content: flex-start;
            align-items: center;
            justify-content: flex-end;
            @media print {
                font-size: 12px;
                padding: 15px 0;
                display: block;
            }
        `,
    },
    paymentInfoData: {
        css: css`
            width: 15%;
            flex-basis: 15%;
            font-size: 14px;
            font-weight: 800;
            font-stretch: normal;
            font-style: normal;
            line-height: 1;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            text-transform: capitalize;
            white-space: normal;

            @media print {
                font-size: 12px;
                font-weight: normal;
                padding: 15px 0;
                width: 20%;
                flex-basis: 20%;
                display: block;
                margin-left: 30px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        display: none;
                    `,
                    css`
                        justify-content: flex-end;
                        padding-right: 40px;
                        border-right: solid 2px ${ADIColors.white2};
                        display: flex;
                        flex-flow: row nowrap;
                        flex-grow: 1;
                        align-items: center;
                    `,
                    css`
                        justify-content: flex-end;
                        padding-right: 40px;
                        border-right: solid 2px ${ADIColors.white2};
                        display: flex;
                        flex-flow: row nowrap;
                        flex-grow: 1;
                        align-items: center;
                    `,
                ])}
        `,
    },
    paymentInfoDataForTablet: {
        css: css`
            width: 100%;
            justify-content: flex-end;
            flex-basis: 100%;
            font-size: 16px;
            font-weight: 800;
            font-stretch: normal;
            font-style: normal;
            line-height: 1;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            text-transform: capitalize;
            display: flex;
            flex-flow: row nowrap;
            flex-grow: 1;
            white-space: normal;
            align-items: center;
            @media print {
                font-size: 12px;
                padding: 15px 0;
            }
        `,
    },
    deleteInfoData: {
        css: css`
            width: 7.2572%;
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            text-transform: capitalize;
            white-space: normal;
            @media print {
                font-size: 12px;
                padding: 15px 0;
                width: 0px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 9.2572% !important;
                        display: flex;
                        flex-flow: row nowrap;
                        flex-grow: 1;
                        justify-content: flex-end;
                        align-items: center;
                    `,
                    css`
                        flex-basis: 9.2572% !important;
                        display: flex;
                        flex-flow: row nowrap;
                        flex-grow: 1;
                        justify-content: flex-end;
                        align-items: center;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        flex-basis: 9.2572% !important;
                        display: flex;
                        flex-flow: row nowrap;
                        flex-grow: 1;
                        justify-content: center;
                        align-items: center;
                    `,
                    css`
                        flex-basis: 9.2572% !important;
                        display: flex;
                        flex-flow: row nowrap;
                        flex-grow: 1;
                        justify-content: center;
                        align-items: center;
                    `,
                ])}
        `,
    },
    deleteInfoDataForTablet: {
        css: css`
            width: 7.2572%;
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1;
            letter-spacing: normal;
            color: ${getColor("text.main")};
            text-transform: capitalize;
            white-space: normal;
            flex-basis: 9.2572% !important;
            display: flex;
            flex-flow: row nowrap;
            flex-grow: 1;
            align-items: center;
            justify-content: flex-start;
            position: absolute;
            background: transparent;
            button {
                margin-top: 5px;
            }
            @media print {
                font-size: 12px;
                padding: 15px 0;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: block;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                ])}
        `,
    },
    typeInfoHeader: {
        css: css`
            flex-basis: 12.22%;
            width: 12.22%;
            text-transform: capitalize;
            padding-right: 30px;
            font-size: 16px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.13;
            letter-spacing: normal;
            color: ${getColor("text.main")} !important;
            display: flex;
            flex-flow: row nowrap;
            flex-grow: 1;
            @media print {
                font-size: 12px;
                padding: 15px 0;
            }
        `,
    },
    invoiceInfoHeader: {
        css: css`
            flex-basis: 8.94%;
            width: 8.94%;
            text-transform: capitalize;
            padding-right: 30px;
            font-size: 16px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.13;
            letter-spacing: normal;
            color: ${getColor("text.main")} !important;
            display: flex;
            flex-flow: row nowrap;
            flex-grow: 1;
            @media print {
                font-size: 12px;
                padding: 15px 0;
            }
        `,
    },
    poInfoHeader: {
        css: css`
            flex-basis: 15.944%;
            width: 15.944%;
            text-transform: capitalize;
            padding-right: 30px;
            font-size: 16px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.13;
            letter-spacing: normal;
            color: ${getColor("text.main")} !important;
            display: flex;
            flex-flow: row nowrap;
            flex-grow: 1;
            @media print {
                font-size: 12px;
                padding: 15px 0;
            }
        `,
    },
    dueDateInfoHeader: {
        css: css`
            flex-basis: 6.76%;
            width: 6.76%;
            max-width: 149px;
            text-transform: capitalize;
            font-size: 16px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.13;
            letter-spacing: normal;
            color: ${getColor("text.main")} !important;
            display: flex;
            flex-flow: row nowrap;
            flex-grow: 1;
            @media print {
                font-size: 12px;
                padding: 15px 0;
                flex-basis: 15%;
                width: 15%;
            }
        `,
    },
    balanceInfoHeader: {
        css: css`
            flex-basis: 9.91%;
            width: 9.91%;
            text-transform: capitalize;
            font-size: 16px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.13;
            letter-spacing: normal;
            color: ${getColor("text.main")} !important;
            display: flex;
            justify-content: flex-end;
            flex-flow: row nowrap;
            flex-grow: 1;
            @media print {
                font-size: 12px;
                padding: 15px 0;
                display: block;
            }
        `,
    },
    paymentInfoHeader: {
        css: css`
            width: 15%;
            flex-basis: 15%;
            text-transform: capitalize;
            font-size: 16px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.13;
            letter-spacing: normal;
            color: ${getColor("text.main")} !important;
            @media print {
                font-size: 12px;
                padding: 15px 0;
                border: none;
                display: block;
                width: 15%;
                flex-basis: 15%;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        display: none;
                    `,
                    css`
                        padding-right: 40px;
                        border-right: solid 2px ${ADIColors.white2};
                        display: flex;
                        justify-content: flex-end;
                        flex-flow: row nowrap;
                        flex-grow: 1;
                    `,
                    css`
                        padding-right: 40px;
                        border-right: solid 2px ${ADIColors.white2};
                        display: flex;
                        justify-content: flex-end;
                        flex-flow: row nowrap;
                        flex-grow: 1;
                    `,
                ])}
        `,
    },
    deleteInfoHeader: {
        css: css`
            flex-basis: 9.2572% !important;
            width: 7.2572%;
            text-transform: capitalize;
            font-size: 16px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.13;
            letter-spacing: normal;
            color: ${getColor("text.main")} !important;
            @media print {
                font-size: 12px;
                padding: 15px 0;
                width: 0px;
                flex-basis: 0px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        display: none;
                    `,
                    css`
                        padding-right: 24px;
                        display: flex;
                        justify-content: center;
                        flex-flow: row nowrap;
                        flex-grow: 1;
                    `,
                    css`
                        padding-right: 24px;
                        display: flex;
                        justify-content: center;
                        flex-flow: row nowrap;
                        flex-grow: 1;
                    `,
                ])}
        `,
    },
    paymentTotalContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        margin-right: 40px;
                    `,
                    css`
                        margin-right: calc(9.2572% + 51px);
                    `,
                    css`
                        margin-right: calc(9.2572% + 51px);
                    `,
                ])}
            display: flex;
            justify-content: flex-end;
            padding-bottom: 4px;
        `,
    },
    totalAmountText: {
        css: css`
            font-size: 16px;
            font-weight: 600;
            font-stretch: normal;
            font-style: normal;
            line-height: 3.88;
            letter-spacing: normal;
            color: ${ADIColors.darkBlack};
        `,
    },
    totalAmountData: {
        css: css`
            font-size: 16px;
            font-weight: 800;
            font-stretch: normal;
            font-style: normal;
            line-height: 3.8;
            letter-spacing: normal;
            color: ${ADIColors.darkBlack};
            margin-left: 10px;
        `,
    },
    deleteButtonStyle: {
        css: css`
            border: none;
            background: ${ADIColors.white};
            span svg path {
                fill: ${ADIColors.blue1} !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0;
                    `,
                    css`
                        padding: 0;
                    `,
                    css`
                        padding: 0;
                    `,
                    null,
                    null,
                ])};
        `,
    },
    invoiceContainerForTablet: {
        css: css`
            margin-top: 20px;
            display: flex;
            flex-direction: column;
            gap: 20px;
        `,
    },
    eachInvoiceContainerForMobile: {
        css: css`
            display: flex;
            padding: 10px;
            flex-direction: column;
            background-color: ${ADIColors.white};
        `,
    },
    invoiceForMobileRowOne: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid ${ADIColors.gray2};
            padding: 5px 0;
        `,
    },
    invoiceForMobileRowTwo: {
        css: css`
            display: flex;
            gap: 5px;
            flex-direction: column;
            font-size: 14px;
            > div:first-child {
                display: flex;
                justify-content: space-between;
                > div {
                    display: inline-flex;
                    width: 40%;
                    justify-content: space-between;
                    > div {
                        font-weight: bold;
                    }
                }
            }
            border-bottom: 1px solid ${ADIColors.gray2};
            padding: 15px 0;
        `,
    },
    mobileSpanStyle: {
        css: css`
            > span {
                display: inline-block !important;
                padding-left: 5px;
            }
        `,
    },
    invoiceForMobileRowThree: {
        css: css`
            padding: 15px 0;
            display: flex;
            justify-content: flex-end;
            font-weight: bold;
            > span {
                padding-left: 5px;
            }
        `,
    },
    emailIcon: {
        css: css`
            position: relative;
            width: 30px;
            display: inline-block;
            > a {
                > span {
                    position: absolute;
                    top: 2px;
                }
            }
            @media print {
                display: none !important;
            }
        `,
    },
};

const styles = invoiceRecapStyles;

const ADIInvoiceRecap: React.FunctionComponent<Props> = ({
    invoicePaymentRecapData,
    adiLoadInvoicePaymentRecap,
    currencySymbol,
    adiDeleteInvoicePaymentRecap,
    isLoading,
    invoicesLink,
    isPaymentCompleted,
}: Props) => {
    const history = useHistory();
    const [isMobile, setIsMobile] = React.useState<boolean>(false);
    const [isPaymentDone, setPaymentDone] = React.useState<boolean>(false);
    const initialRender = React.useRef(true);
    const dispatch = useDispatch();

    React.useEffect(() => {
        adiLoadInvoicePaymentRecap({
            customerSequence: "-1",
            page: 1,
            pageSize: 20,
            showOpenOnly: true,
            properties: JSON.stringify({ filterpaymentrecap: true }),
            sort: "invoiceDate DESC",
            onSuccess(result) {
                initialRender.current = false;
            },
        });

        const onWindowResize = () => {
            if (window.innerWidth >= 768) {
                setIsMobile(false);
            } else {
                setIsMobile(true);
            }
        };
        onWindowResize();

        if (typeof window !== "undefined") {
            window.addEventListener("resize", onWindowResize);
        }

        return () => {
            if (typeof window !== "undefined") {
                window.removeEventListener("resize", onWindowResize);
            }
        };
    }, []);

    React.useEffect(() => {
        if (isPaymentCompleted) {
            setPaymentDone(isPaymentCompleted);
        }
    }, [isPaymentCompleted]);

    React.useEffect(() => {
        if (!invoicePaymentRecapData?.invoices?.length && !isLoading && invoicesLink && !initialRender.current) {
            history.push(invoicesLink);
        }
    }, [invoicePaymentRecapData]);

    if (!invoicePaymentRecapData || !invoicePaymentRecapData.invoices?.length) {
        return null;
    }

    const paymentTotal = invoicePaymentRecapData.invoices
        ?.map(invoiceLine => invoiceLine.otherCharges)
        .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

    const formatNumberWithTwoDecimals = (number: number | undefined) => {
        if (!number) {
            return null;
        }
        const formattedNumber = number.toFixed(2);
        return formattedNumber;
    };

    const deleteInvoiceLine = (invoiceNumber: string) => {
        initialRender.current = true;
        const invoiceNumberWithoutSpace = invoiceNumber.trim();

        const selectedInvoicesString = sessionStorage.getItem("selectedInvoices");
        if (selectedInvoicesString !== null && selectedInvoicesString !== undefined) {
            const selectedSessionInvoices: InvoicePaymentModel[] = JSON.parse(selectedInvoicesString);

            const updatedSavedInvoices = selectedSessionInvoices.filter(
                invoiceItem => invoiceItem.InvoiceId !== invoiceNumber,
            );
            sessionStorage.setItem("selectedInvoices", JSON.stringify(updatedSavedInvoices));
        }

        adiDeleteInvoicePaymentRecap({
            invoiceNumber: invoiceNumberWithoutSpace,
        });
        adiLoadInvoicePaymentRecap({
            customerSequence: "-1",
            page: 1,
            pageSize: 20,
            showOpenOnly: true,
            properties: JSON.stringify({ filterpaymentrecap: true }),
            sort: "invoiceDate DESC",
            onSuccess(result) {
                initialRender.current = false;
            },
        });
    };

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    const formatYearData = (dateString: string) => {
        if (!dateString) return;

        const date = new Date(dateString);

        function getAbbreviatedMonthName(month: number) {
            month = Math.max(0, Math.min(11, month));
            return new Date(0, month).toLocaleDateString("en-US", { month: "short" });
        }

        const month = getAbbreviatedMonthName(date.getMonth());
        const day = date.getDate();
        const year = date.getFullYear();
        const formattedDate = month + " " + day + ", " + year;
        return formattedDate;
    };

    return (
        <StyledWrapper {...styles.mainContainer}>
            {isMobile ? (
                <Hidden above="sm">
                    <StyledWrapper {...styles.creditRepContainer}>
                        <StyledWrapper>
                            <span>
                                <strong>Credit Rep:</strong>{" "}
                                {invoicePaymentRecapData.properties.creditRep.toLowerCase()}
                            </span>
                            <span>Phone: {formatPhoneNumber(invoicePaymentRecapData.properties.phoneNumber)}</span> |

                            <Typography as="span" {...styles.emailIcon}>
                                <Link href={`mailto:${invoicePaymentRecapData.properties.email.toLowerCase()}`}>
                                    <MailIcon />
                                </Link>
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.invoiceContainerForTablet}>
                        {invoicePaymentRecapData.invoices &&
                            invoicePaymentRecapData.invoices?.map(invoiceLine => (
                                <StyledWrapper key={invoiceLine.id} {...styles.eachInvoiceContainerForMobile}>
                                    <StyledWrapper {...styles.invoiceForMobileRowOne}>
                                        <StyledWrapper>{formatDate(invoiceLine.invoiceDate.toString())}</StyledWrapper>
                                        {!isPaymentDone && (
                                            <StyledWrapper {...styles.deleteInfoData}>
                                                <Button
                                                    variant="secondary"
                                                    {...styles.deleteButtonStyle}
                                                    onClick={() => deleteInvoiceLine(invoiceLine.invoiceNumber)}
                                                >
                                                    <ButtonIcon src={TrashIcon} />
                                                </Button>
                                            </StyledWrapper>
                                        )}
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.invoiceForMobileRowTwo}>
                                        <StyledWrapper>
                                            {translate("Invoice #")} : {invoiceLine.invoiceNumber}
                                            <StyledWrapper>
                                                <StyledWrapper>{translate("Balance")}:</StyledWrapper>
                                                <StyledWrapper>
                                                    {currencySymbol}
                                                    {invoiceLine.invoiceTotal.toString().indexOf("-") === -1
                                                        ? invoiceLine.invoiceTotal
                                                        : `(${Math.abs(invoiceLine.invoiceTotal)})`}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.mobileSpanStyle}>
                                            {translate("Type: ")}: <span>{invoiceLine.invoiceType}</span>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.mobileSpanStyle}>
                                            {translate("Due Date")}:
                                            <span>{formatDate(invoiceLine.dueDate.toString())}</span>
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.mobileSpanStyle}>
                                            {translate("PO #")}:<span>${invoiceLine?.customerPO?.trim()}</span>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                    <StyledWrapper {...styles.invoiceForMobileRowThree}>
                                        {translate("Payment: ")}
                                        <span>
                                            {currencySymbol}
                                            {invoiceLine.otherCharges.toString().indexOf("-") === -1
                                                ? invoiceLine.otherCharges.toLocaleString()
                                                : `(${Math.abs(invoiceLine.otherCharges)})`}
                                        </span>
                                    </StyledWrapper>
                                </StyledWrapper>
                            ))}
                    </StyledWrapper>
                    <StyledWrapper {...styles.paymentTotalContainer}>
                        <Typography as="span" {...styles.totalAmountText}>
                            Payment Total:
                        </Typography>
                        <Typography as="span" {...styles.totalAmountData}>
                            {paymentTotal >= 0 ? (
                                <>
                                    {currencySymbol}
                                    {paymentTotal.toFixed(2).toLocaleString()}
                                </>
                            ) : (
                                `(${currencySymbol}${paymentTotal.toFixed(2).toLocaleString().split("-")[1]})`
                            )}
                        </Typography>
                    </StyledWrapper>
                </Hidden>
            ) : (
                <StyledWrapper {...styles.desktopWrapper}>
                    <StyledWrapper {...styles.creditRepContainer}>
                        <StyledWrapper>
                            <span>
                                <strong>Credit Rep:</strong>{" "}
                                {invoicePaymentRecapData.properties.creditRep.toLowerCase()}
                            </span>{" "}
                            |<span>Phone: {formatPhoneNumber(invoicePaymentRecapData.properties.phoneNumber)}</span> |

                            <Typography as="span" {...styles.emailIcon}>
                                <Link href={`mailto:${invoicePaymentRecapData.properties.email.toLowerCase()}`}>
                                    <MailIcon />
                                </Link>
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper>
                        <StyledWrapper>
                            <StyledWrapper {...styles.invoiceInfoHeaderContainer}>
                                <StyledWrapper {...styles.dateInfoHeader}>Date</StyledWrapper>
                                <StyledWrapper {...styles.typeInfoHeader}>Type</StyledWrapper>
                                <StyledWrapper {...styles.invoiceInfoHeader}>Invoice</StyledWrapper>
                                <StyledWrapper {...styles.poInfoHeader}>PO #</StyledWrapper>
                                <StyledWrapper {...styles.dueDateInfoHeader}>Due Date</StyledWrapper>
                                <StyledWrapper {...styles.balanceInfoHeader}>Balance</StyledWrapper>
                                <StyledWrapper {...styles.paymentInfoHeader}>Payment</StyledWrapper>
                                {!isPaymentCompleted && (
                                    <StyledWrapper {...styles.deleteInfoHeader}>Delete</StyledWrapper>
                                )}
                            </StyledWrapper>
                            {invoicePaymentRecapData.invoices &&
                                invoicePaymentRecapData.invoices.map(invoiceLine => (
                                    <>
                                        <StyledWrapper key={invoiceLine.id} {...styles.invoiceInfoDataContainer}>
                                            <StyledWrapper {...styles.invoiceInfoDataDesktopContainer}>
                                                <StyledWrapper {...styles.dateInfoData}>
                                                    {/* <LocalizedDateTime
                                                        dateTime={invoiceLine.invoiceDate}
                                                    ></LocalizedDateTime> */}
                                                    {formatYearData(invoiceLine.invoiceDate.toString())}
                                                </StyledWrapper>
                                                <StyledWrapper {...styles.typeInfoData}>
                                                    <Typography as="span">{invoiceLine.invoiceType}</Typography>
                                                </StyledWrapper>
                                                <StyledWrapper {...styles.invoiceInfoData}>
                                                    {invoiceLine.invoiceNumber}
                                                </StyledWrapper>
                                                <StyledWrapper {...styles.poInfoData}>
                                                    {invoiceLine.customerPO}
                                                </StyledWrapper>
                                                <StyledWrapper {...styles.dueDateInfoData}>
                                                    {/* <LocalizedDateTime
                                                        dateTime={invoiceLine.dueDate}
                                                    ></LocalizedDateTime> */}
                                                    {formatYearData(invoiceLine.dueDate.toString())}
                                                </StyledWrapper>
                                                <StyledWrapper {...styles.balanceInfoData}>
                                                    {currencySymbol}
                                                    {invoiceLine.invoiceTotal.toString().indexOf("-") === -1
                                                        ? invoiceLine.invoiceTotal.toLocaleString()
                                                        : `(${Math.abs(invoiceLine.invoiceTotal)})`}
                                                </StyledWrapper>
                                                <StyledWrapper {...styles.paymentInfoData}>
                                                    {currencySymbol}
                                                    {invoiceLine.otherCharges.toString().indexOf("-") === -1
                                                        ? invoiceLine.otherCharges.toLocaleString()
                                                        : `(${Math.abs(invoiceLine.otherCharges)})`}
                                                </StyledWrapper>
                                                {!isPaymentDone && (
                                                    <StyledWrapper {...styles.deleteInfoData}>
                                                        <Button
                                                            variant="secondary"
                                                            {...styles.deleteButtonStyle}
                                                            onClick={() => deleteInvoiceLine(invoiceLine.invoiceNumber)}
                                                        >
                                                            <ButtonIcon src={TrashIcon} />
                                                        </Button>
                                                    </StyledWrapper>
                                                )}
                                            </StyledWrapper>

                                            {!isPaymentDone && (
                                                <StyledWrapper {...styles.deleteInfoDataForTablet}>
                                                    <Button
                                                        variant="secondary"
                                                        {...styles.deleteButtonStyle}
                                                        onClick={() => deleteInvoiceLine(invoiceLine.invoiceNumber)}
                                                    >
                                                        <ButtonIcon src={TrashIcon} />
                                                    </Button>
                                                </StyledWrapper>
                                            )}
                                            <StyledWrapper {...styles.invoiceInfoDataTabletContainer}>
                                                <StyledWrapper {...styles.paymentInfoDataForTablet}>
                                                    {translate("Payment:")} {currencySymbol}
                                                    {invoiceLine.otherCharges.toString().indexOf("-") === -1
                                                        ? invoiceLine.otherCharges.toLocaleString()
                                                        : `(${Math.abs(invoiceLine.otherCharges)})`}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </>
                                ))}
                        </StyledWrapper>
                        <StyledWrapper {...styles.paymentTotalContainer}>
                            <Typography as="span" {...styles.totalAmountText}>
                                Payment Total:
                            </Typography>
                            <Typography as="span" {...styles.totalAmountData}>
                                {paymentTotal >= 0 ? (
                                    <>
                                        {currencySymbol}
                                        {paymentTotal.toFixed(2).toLocaleString()}
                                    </>
                                ) : (
                                    `(${currencySymbol}${paymentTotal.toFixed(2).toString().split("-")[1]})`
                                )}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ADIInvoiceRecap),
    definition: {
        group: "My Account",
        displayName: "ADI Invoice Payment Recap",
    },
};

export default widgetModule;
